import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import useTokenFromUrl from "../../hooks/useTokenFromUrl";
import { useAuth } from "../../context/AuthContext";
import { fetchActiveCourses, fetchDemoRequest, fetchMyEnrollments } from "./clientCourseService";
import CourseCard from "./CourseCard";
import DemoRequestModal from "./DemoRequestModal";
import "./courses.css";

const CoursesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loading: tokenLoading } = useTokenFromUrl();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demoCourseId, setDemoCourseId] = useState(null);
  const [demoCourse, setDemoCourse] = useState(null);
  const [bookedCourseIds, setBookedCourseIds] = useState(new Set());
  // set of batchIds where user is ACTIVE or PENDING_PAYMENT
  const [enrolledBatchIds, setEnrolledBatchIds] = useState(new Set());
  // map of courseId -> count of active enrollments (for subtle badge only)
  const [activeBatchCountMap, setActiveBatchCountMap] = useState({});

  const handleDemo = (courseId) => {
    if (!user) { navigate("/login?redirect=/courses"); return; }
    const course = courses.find((c) => c.id === courseId);
    setDemoCourse(course);
    setDemoCourseId(courseId);
  };

  useEffect(() => {
    if (tokenLoading) return;
    fetchActiveCourses()
      .then((res) => setCourses(res.data || []))
      .catch(() => setCourses([]))
      .finally(() => setLoading(false));
  }, [tokenLoading]);

  useEffect(() => {
    if (!user || courses.length === 0) return;

    // fetch demo status + enrollments in parallel
    Promise.allSettled([
      Promise.allSettled(courses.map((c) => fetchDemoRequest(c.id))),
      fetchMyEnrollments(),
    ]).then(([demoRes, enrollRes]) => {
      if (demoRes.status === "fulfilled") {
        const booked = new Set();
        demoRes.value.forEach((r, i) => {
          if (r.status === "fulfilled") booked.add(courses[i].id);
        });
        setBookedCourseIds(booked);
      }

      if (enrollRes.status === "fulfilled") {
        const batchIds = new Set();
        const countMap = {};
        (enrollRes.value.data || []).forEach((e) => {
          if (["ACTIVE", "PENDING_PAYMENT"].includes(e.status)) {
            batchIds.add(String(e.batchId));
          }
          if (e.status === "ACTIVE") {
            countMap[e.courseId] = (countMap[e.courseId] || 0) + 1;
          }
        });
        setEnrolledBatchIds(batchIds);
        setActiveBatchCountMap(countMap);
      }
    });
  }, [user, courses]);

  if (tokenLoading) {
    return (
      <>
        <Navbar />
        <div className="cc-loading"><div className="cc-spinner" /><p>Loading…</p></div>
      </>
    );
  }

  const otherCourses = courses;

  return (
    <>
      <Navbar />

      <div className="cc-hero">
        <h1>Live Zoom Courses</h1>
        <p>Structured programs with daily live sessions and personal assessment</p>
      </div>

      <div className="cc-page">
        {loading && (
          <div className="cc-loading">
            <div className="cc-spinner" />
            <p>Loading courses…</p>
          </div>
        )}

        {!loading && courses.length === 0 && (
          <div className="cc-empty">
            <div className="cc-empty-icon">📚</div>
            <p>No courses available right now. Check back soon!</p>
          </div>
        )}

        {/* ── All courses ── */}
        {!loading && otherCourses.length > 0 && (
          <div className="cc-grid">
            {otherCourses.map((c) => (
              <CourseCard
                key={c.id}
                course={c}
                onView={(id) => navigate(`/courses/${id}`)}
                onDemo={handleDemo}
                demoBooked={bookedCourseIds.has(c.id)}
                activeBatchCount={activeBatchCountMap[c.id] || 0}
              />
            ))}
          </div>
        )}
      </div>

      {demoCourseId && (
        <DemoRequestModal
          courseId={demoCourseId}
          course={demoCourse}
          onSuccess={() => {
            setBookedCourseIds((prev) => new Set([...prev, demoCourseId]));
            setDemoCourseId(null);
            setDemoCourse(null);
          }}
          onClose={() => { setDemoCourseId(null); setDemoCourse(null); }}
        />
      )}
    </>
  );
};

export default CoursesPage;
